import { SUITS } from '../scoring/ScoringConstants.js';
import { Card } from './Card.js';

/**
 * Hand Model managing a collection of cards (hand, crib, or played pile).
 */
export class Hand {
  constructor(cards = []) {
    this.cards = [];
    if (Array.isArray(cards)) {
      this.add(cards);
    }
  }

  get length() {
    return this.cards.length;
  }

  get isEmpty() {
    return this.cards.length === 0;
  }

  add(cardOrCards) {
    if (!cardOrCards) return this;
    if (Array.isArray(cardOrCards)) {
      for (const item of cardOrCards) {
        if (item) this.cards.push(Card.from(item));
      }
    } else {
      this.cards.push(Card.from(cardOrCards));
    }
    return this;
  }

  remove(cardId) {
    const idx = this.cards.findIndex(c => c.id === cardId);
    if (idx >= 0) {
      return this.cards.splice(idx, 1)[0];
    }
    return null;
  }

  findById(cardId) {
    return this.cards.find(c => c.id === cardId) || null;
  }

  sort() {
    this.cards.sort((a, b) => (a.r - b.r) || (SUITS.indexOf(a.s) - SUITS.indexOf(b.s)));
    return this;
  }

  getPlayableCards(currentCount = 0) {
    return this.cards.filter(c => c.fits(currentCount));
  }

  hasPlayableCard(currentCount = 0) {
    return this.cards.some(c => c.fits(currentCount));
  }

  totalNominalValue() {
    return this.cards.reduce((sum, c) => sum + c.val, 0);
  }

  clear() {
    this.cards = [];
    return this;
  }

  clone() {
    return new Hand(this.cards.map(c => c.clone()));
  }

  [Symbol.iterator]() {
    return this.cards[Symbol.iterator]();
  }

  forEach(fn) {
    return this.cards.forEach(fn);
  }

  map(fn) {
    return this.cards.map(fn);
  }

  filter(fn) {
    return this.cards.filter(fn);
  }

  some(fn) {
    return this.cards.some(fn);
  }

  every(fn) {
    return this.cards.every(fn);
  }

  find(fn) {
    return this.cards.find(fn);
  }

  findIndex(fn) {
    return this.cards.findIndex(fn);
  }

  slice(start, end) {
    return this.cards.slice(start, end);
  }

  pop() {
    return this.cards.pop();
  }

  splice(start, deleteCount, ...items) {
    return this.cards.splice(start, deleteCount, ...items);
  }

  toPlain() {
    return this.cards.map(c => c.toPlain());
  }

  static sort(cards = []) {
    return cards.slice().sort((a, b) => {
      const ar = (a && (a.r !== undefined ? a.r : a.rank)) || 0;
      const br = (b && (b.r !== undefined ? b.r : b.rank)) || 0;
      const as = (a && (a.s || a.suit)) || 'S';
      const bs = (b && (b.s || b.suit)) || 'S';
      return (ar - br) || (SUITS.indexOf(as) - SUITS.indexOf(bs));
    });
  }
}
